import {
    ClickAwayListener,
    Grid2 as Grid,
    PopoverPosition,
    Stack,
    Typography,
} from '@mui/material';
import React, { CSSProperties, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, VariableSizeList } from 'react-window';
import useBreakpoints from '../../../../hooks/useBreakpoint';
import {
    IEplassProjectData,
    ProjectDisplayType,
    ProjectViewType,
} from '../../../../redux/user/sliceUserProjects';
import { IAppReduxStore } from '../../../../redux/store';
import { defaultBorder } from '../../../../Theme';
import { sortData } from '../../../General/DynamicTree/DynamicTreeUtils';
import ResizeableDrawer from '../../../General/ResizeableDrawer/ResizeableDrawer';
import ProjectContextMenu, { IProjectContextMenuConfig } from './ProjectContextMenu';
import ProjectListSidebar from './ProjectListSidebar';
import ProjectRow from './ProjectRow';
import ProjectTile from './ProjectTile';
import ProjectTree from './ProjectTree';
import { buildProjectGroups, filterProjects } from './ProjectUtils';
import { leftSidepanelMinWidth, sidepanelMaxWidth } from '../../../../Constants';

const C_RowHeight = 64;
const C_TileHeight = 120;

/**
 * Copnditionally returns a value based on the truthyness of a given statement.
 * @param condition - Statement to be evaluated as a boolean.
 * @param valueIfTrue - Value to return if the condition is true.
 * @param valueIfFalse - Value to return if the condition is false.
 */
const getValue = <A, B, C>(condition: A, valueIfTrue: B, valueIfFalse: C) =>
    condition ? valueIfTrue : valueIfFalse;

/**
 * Checks to see if a given project's replicaId is present in the user's favourites.
 * @param project - Target project.
 * @param favourites - List of user project favourites.
 * @returns Project favourite status.
 */
const isFavourite = (project: IEplassProjectData, favourites: string[]) =>
    favourites.indexOf(project.replicaId) >= 0;

/**
 * Component that shows the user's projects.
 */
export default () => {
    /** Determine screen size. */
    const { isDesktop } = useBreakpoints();

    /** Get relevant values from Redux store. */
    const displayType = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.displayType,
    );
    const activeCategory = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.activeCategory,
    );
    const sortAz = useSelector((store: IAppReduxStore) => store.userProjects.settings.sortAz);
    const filter = useSelector((store: IAppReduxStore) => store.userProjects.settings.filter);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);
    const viewType = useSelector((store: IAppReduxStore) => store.userProjects.settings.viewType);
    const favourites = useSelector(
        (store: IAppReduxStore) => store.userSettings.data?.favourites?.projects,
    );
    const sidebarOpen = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.sidebarOpen,
    );

    /** State slice to track the position of the project context menu. */
    const [contextMenuPosition, setContextMenuPosition] = useState<PopoverPosition>(null);

    /** State slice to track which project the context menu has been opened for. */
    const [contextMenuProject, setContextMenuProject] = useState<IEplassProjectData>(null);

    /** State slice that stores the number of project tiles that can be shown next to one another in the viewport. */
    const [colCount, setColCount] = useState<number>(1);

    /** Ref for syncing scroll position. */
    const listRef = useRef<HTMLElement>();

    /** No projects provided - show nothing. */
    if (!projects) {
        return <></>;
    }

    /** Configuration object for the project context menu. */
    const contextMenuConfig: IProjectContextMenuConfig = {
        position: [contextMenuPosition, setContextMenuPosition],
        project: contextMenuProject,
        /**
         * Makes the context menu visible.
         * @param event - Mouse event.
         * @param project - (Optional) target project.
         */
        callback: (event: React.MouseEvent, project?: IEplassProjectData): void => {
            event.preventDefault();

            setContextMenuProject(project);

            setContextMenuPosition(
                getValue(
                    !contextMenuPosition,
                    { left: event.clientX + 2, top: event.clientY - 6 },
                    null,
                ),
            );
        },
    };

    /**
     * Callback that allows the react-window list to be re-rendered when it is resized.
     */
    const onListSizeChange = useCallback(
        (ref: React.MutableRefObject<FixedSizeList>, width: number) => {
            if (ref) {
                ref.current?.resetAfterIndex(0);

                setColCount(Math.max(Math.floor(width / 400), 1));
            }
        },
        [setColCount],
    );

    /** User's view type is 'Tree'/Hierarchy, so render a ProjectTree component. */
    if (displayType === ProjectDisplayType.Tree) {
        const treeStyle: CSSProperties = {
            position: 'absolute',
            overflow: 'auto',
            height: '100%',
            width: '100%',
            padding: '16px',
            left: 0,
            top: 0,
        };

        return (
            <ProjectTree
                projectConfig={{ favourites }}
                contextMenu={contextMenuConfig}
                style={treeStyle}
                sortAz={sortAz}
            />
        );
    }

    /** Whether to show project tiles or not. */
    const isTileView = displayType === ProjectDisplayType.Tile;

    /** Data used for rendering the react-window list. */
    const listData = processListData({
        projects,
        favourites,
        filter,
        viewType,
        sortAz,
        colCount,
        isTileView,
        activeCategory,
    });

    /**
     * Creates the props needed to render a given project in the view.
     * @param project - Target project.
     * @returns Props object for ProjectRowTile or ProjectRow.
     */
    const rowProps = (project: IEplassProjectData) => ({
        key: project.replicaId,
        project: project,
        favourite: isFavourite(project, favourites),
        size: colCount,
    });

    /**
     * Renders a row of data in the react-window list.
     */
    const renderRow = ({ index, style }: { index: number; style: CSSProperties }) => {
        const item: string | IEplassProjectData | IEplassProjectData[] = listData[index];

        /** This is a header, so return a h6 Typography element. */
        if (typeof item === 'string') {
            return (
                <Typography
                    variant='h6'
                    style={style}
                    sx={{ height: C_RowHeight, m: 0, pt: 4, pl: 5.25 }}
                >
                    {item.slice(0, -1)}
                </Typography>
            );
        }

        /** If thre are multiple items in the data-row, then create a MUI Grid to display them. */
        if (Array.isArray(item)) {
            return (
                <Grid container sx={{ ...style, m: 0, px: 4 }} spacing={1}>
                    {item.map(x => {
                        const { key, ...otherProps } = rowProps(x);
                        return (
                            <ProjectTile
                                key={key}
                                {...otherProps}
                                contextMenu={contextMenuConfig}
                            />
                        );
                    })}
                </Grid>
            );
        }

        const { key, ...otherProps } = rowProps(item);

        /** Only a single item will be shown per row. */
        return getValue(
            isTileView,
            <ProjectTile key={key} {...otherProps} style={style} contextMenu={contextMenuConfig} />,
            <ProjectRow key={key} {...otherProps} style={style} contextMenu={contextMenuConfig} />,
        );
    };

    /**
     * Creates the react-window list to be rendered.
     * @param width - Element width.
     * @param height - Element height.
     */
    const getList = (width: number, height: number) => {
        /**
         * Gets the row height.
         * @param index - Row index.
         * @returns Row height.
         */
        const getItemHeight = (index: number) =>
            getValue(typeof listData[index] === 'string', C_RowHeight, C_TileHeight);

        /**
         * Passes a ref to the list which syncs scroll position and re-renders the list when width changes.
         * @param target - Target JSX Element.
         */
        const passRef = (target: HTMLElement) => {
            /** Update listRef so that scroll position can be synced. */
            listRef.current = target;

            /** Only need to trigger onListSizeChange if displayType is 'Tiles'. */
            isTileView && onListSizeChange(listRef, width);
        };

        /** Base props that are passed to VariableSizeList or FixedSizeList. */
        const listProps = {
            /** Pass element dimensions from AutoSizer. */
            height: height,
            width: width,
            /** Tell react-window how many rows it will need to render. */
            itemCount: listData.length,
            /** Pass the ref function to the  */
            ref: passRef,
            /** Item size is dynamic if displayType is 'Tiles'; otherwise it is static */
            itemSize: getValue(isTileView, getItemHeight, C_RowHeight),
            /** Children is the function responsible for rendering rows in the List. */
            children: renderRow,
        };

        /** Wrap list in an element to have the corrent outer spacing. */
        return (
            <>
                {getValue(
                    isTileView,
                    <VariableSizeList {...listProps} />,
                    <FixedSizeList {...listProps} />,
                )}
                <ProjectContextMenu config={contextMenuConfig} />
            </>
        );
    };

    /** AutoSizer component that contains the project list. */
    const main = <AutoSizer>{({ width, height }) => getList(width, height)}</AutoSizer>;

    /** Mobile/Tablet view. */
    if (!isDesktop) {
        return !sidebarOpen ? (
            <Stack direction='column' sx={{ width: '100%' }}>
                {main}
            </Stack>
        ) : (
            <></>
        );
    }

    /** Desktop view. */
    return (
        <ClickAwayListener onClickAway={() => setContextMenuPosition(null)}>
            <Stack direction='row' sx={{ width: '100%', overflow: 'hidden' }}>
                {sidebarOpen ? (
                    <ResizeableDrawer
                        minWidth={leftSidepanelMinWidth}
                        maxWidth={sidepanelMaxWidth}
                        children={<ProjectListSidebar />}
                    />
                ) : (
                    <Stack
                        sx={{ width: 40, position: 'relative', borderRight: defaultBorder }}
                        children={<ProjectListSidebar />}
                    />
                )}

                <Stack
                    sx={{
                        flexGrow: 4,
                    }}
                >
                    {main}
                </Stack>
            </Stack>
        </ClickAwayListener>
    );
};

/**
 * Processes a collection of user projects so that they can be shown in the Project View.
 * @param projectGroups - Project groups to process.
 * @param opts - Options object.
 */
const processListData = (opts: IProcessListDataOpts) => {
    /** Deconstruct opts. */
    const { activeCategory, colCount, favourites, filter, isTileView, projects, sortAz, viewType } =
        opts;

    /** Create groups of the user's filtered projects. */
    const projectGroups = buildProjectGroups(
        filterProjects(projects, favourites, filter, viewType),
    );

    /** Output data array. */
    const out: Array<string | IEplassProjectData | IEplassProjectData[]> = [];

    /** Create a list of project group names that start with activeCategory (if set). */
    sortData<IEplassProjectData, string>(projectGroups, 'title', {
        stringsAZ: sortAz,
        datesDesc: sortAz,
    })
        .filter(
            x =>
                !activeCategory ||
                new RegExp(
                    `^${activeCategory.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')}(\\\\|$)`,
                    'i',
                ).test(x),
        )
        /** Process the group data to create renderable rows. */
        .forEach(group => {
            out.push(group);

            let arr = [];

            sortData<IEplassProjectData, IEplassProjectData>(projectGroups[group], 'title', {
                stringsAZ: sortAz,
                datesDesc: sortAz,
            }).forEach(project => {
                if (!isTileView || colCount === 1) {
                    out.push(project);

                    return;
                }

                arr.push(project);

                if (arr.length === colCount) {
                    out.push([...arr]);
                    arr = [];
                }
            });

            if (arr.length) {
                out.push(arr);
            }
        });

    return out;
};

/**
 * Interface that describes the configuration options for processListData.
 */
interface IProcessListDataOpts {
    /** Whether to sort the project data alphabetically. */
    sortAz: boolean;

    /** Number of items to show next to one another. */
    colCount: number;

    /** Whether the display type is set to tile view or not. */
    isTileView: boolean;

    /** The user's selected category. */
    activeCategory: string;

    /** Array of user projects. */
    projects: IEplassProjectData[];

    /** List of replicaIds for the user's favourite projects. */
    favourites: string[];

    /** User-defined filter string. */
    filter: string;

    /** Active project view type. */
    viewType: ProjectViewType;
}
